import { config } from 'src/constants';

import {
  // plate size details fields
  PLATE_SIZE_FIELD,
  WELL_FIELD,
  SCALE_FIELD,
  PURIFICATION_FIELD,
  BUFFER_FIELD,
  QUALITY_TESTING_FIELD,
  SHIPPING_FORMAT_FIELD,

  // normalization aliquots fields
  NORMALIZATION,
  VOLUME,
  VOLUME_UNIT,
  CONCENTRATION,
  CONCENTRATION_UNIT,
  ALIQUOTS_CHECKED,
  ALIQUOTS_NUMBER,
  ALIQUOTS_VOLUME,
  REMAINDER_CHECKED,

  // namespaces
  PLATE_SIZE_DETAILS_NAMESPACE,
  NORMALIZATION_ALIQUOTS_NAMESPACE,
  PLATE_POOLING_NAMESPACE,
  REMAINDER_TREATMENT,
  REMAINDER_PLATE_TYPE,
  REMAINDER_VOLUME,
  REMAINDERS_NAMESPACE,
  // form data
  STEPS,
  NORMALIZATION_OPTIONS,
  PLATE_POOLING,
  PLATE_POOLING_OPTIONS,
  PLATE_SIZES,
  AMPLISEQ_DNA,
  DNA,
  AMPLISEQ_RNA,
  RNA,
  SALES_QUOTE_REP_USER,
  CUSTOMER_SERVICE_REPRESENTATIVE_USER,
  DNA_TYPE,
  COUNTRY_CODE_JAPAN,
} from 'src/constants/forms';
import { cartRequestMockHeaders } from 'src/mocks';
import { getCountryCode, getLoggedUserData } from 'src/utils';

import DnaOligoState from './DnaOligoState';
import {
  isUploadFileValid,
  isPlateSizeDetailsValid,
  isNormalizationAliquotsValid,
  isPlatePoolingValid,
  getPooledPlatesListData,
  DEFAULT_DNA_STEPS,
  DNA_STEPS,
} from './helpers';

const urls = {
  addToCart: '/v2/oligos/plates/submit',
  validate: '/v2/oligos/plates/validate'
};

const processRemainders = (remaindersData) => {
  const hasRemainders = remaindersData[REMAINDER_CHECKED]?.value;
  const remainderTreatment = remaindersData[REMAINDER_TREATMENT]?.value;
  const remainderPlateType = remaindersData[REMAINDER_PLATE_TYPE]?.value;
  const remainderVolume = remaindersData[REMAINDER_VOLUME]?.value;

  return {
    hasRemainders,
    remainderTreatment,
    remainderPlateType,
    remainderVolume
  };
};

const hasAliquotsAllowed = (normalization) => (
  normalization !== NORMALIZATION_OPTIONS.NONE &&
  normalization !== NORMALIZATION_OPTIONS.CONCENTRATION_VOLUME
);

const hasRemaindersAllowed = (normalization, platePooling) => hasAliquotsAllowed(normalization) ||
  (platePooling && platePooling !== PLATE_POOLING_OPTIONS.NONE);

export default class DnaOligo extends DnaOligoState {
  static NAME = 'dna_oligo';

  static urls = urls;

  static getCreateConfig() {
    return {
      url: urls.addToCart,
      ...(config.isDevEnv ? { headers: cartRequestMockHeaders } : {}),
    };
  }

  static processRemainders = processRemainders;

  static hasAliquotsAllowed = hasAliquotsAllowed;

  static hasRemaindersAllowed = hasRemaindersAllowed;

  static getDNAType = () => {
    const locationStrings = localStorage.getItem(DNA_TYPE)?.split('?') || [];

    const user = getLoggedUserData();

    if (user?.userRoles?.roles?.includes(CUSTOMER_SERVICE_REPRESENTATIVE_USER ||
        SALES_QUOTE_REP_USER) &&
      locationStrings.includes(AMPLISEQ_DNA)) {
      return AMPLISEQ_DNA;
    }

    if (user?.userRoles?.roles?.includes(CUSTOMER_SERVICE_REPRESENTATIVE_USER ||
        SALES_QUOTE_REP_USER) &&
      locationStrings.includes(AMPLISEQ_RNA)) {
      return AMPLISEQ_RNA;
    }

    return null;
  };

  static getAmpliseqType = (dnaType) => {
    if (dnaType === AMPLISEQ_DNA) {
      return DNA;
    }

    if (dnaType === AMPLISEQ_RNA) {
      return RNA;
    }

    return dnaType;
  };

  static getDefaultStepsState() {
    const countryCode = getCountryCode();
    const dnaType = this.getDNAType();

    if ((dnaType === AMPLISEQ_DNA || dnaType === AMPLISEQ_RNA) &&
    (countryCode === COUNTRY_CODE_JAPAN)) {
      return DEFAULT_DNA_STEPS;
    }

    return DNA_STEPS[countryCode] || DEFAULT_DNA_STEPS;
  }

  static getDnaCartActiveStepState() {
    const steps = this.getDefaultStepsState();

    return steps.map((item, index) => ({
      ...item,
      isActive: index === steps.length - 1,
      isVisited: true,
      isComplete: true
    }));
  }

  static processApiData = (collectedData) => {
    // example of request body in 'Submit the plate configuration to cart' section here:
    // https://confluence.amer.thermo.com/display/ARCLD/Endpoints+for+Oligo+plates+service
    // the same request body applies for validation

    const plateSizeData = collectedData[PLATE_SIZE_DETAILS_NAMESPACE] || {};
    const normalizationAliquotsData = collectedData[NORMALIZATION_ALIQUOTS_NAMESPACE] || {};
    const platePoolingData = collectedData[PLATE_POOLING_NAMESPACE] || {};
    const remaindersData = collectedData[REMAINDERS_NAMESPACE] || {};

    const plateSize = plateSizeData[PLATE_SIZE_FIELD]?.value;
    const wellVolume = plateSizeData[WELL_FIELD]?.value;
    const synthesisScale = plateSizeData[SCALE_FIELD]?.value;
    const purification = plateSizeData[PURIFICATION_FIELD]?.value;
    const buffer = plateSizeData[BUFFER_FIELD]?.value;
    const qualityTestingPercentage = plateSizeData[QUALITY_TESTING_FIELD]?.value;
    const hasQualityTestingPercentage = plateSizeData[QUALITY_TESTING_FIELD]?.isValid;
    const shipFormat = plateSizeData[SHIPPING_FORMAT_FIELD]?.value;

    const normalization = normalizationAliquotsData[NORMALIZATION]?.value;
    const volumeNormalization = normalizationAliquotsData[VOLUME]?.value;
    const volumeNormalizationUom = normalizationAliquotsData[VOLUME_UNIT]?.value;
    const concentrationNormalization = normalizationAliquotsData[CONCENTRATION]?.value;
    const concentrationNormalizationUom = normalizationAliquotsData[CONCENTRATION_UNIT]?.value;
    const hasAliquots = normalizationAliquotsData[ALIQUOTS_CHECKED]?.value;
    const aliquotsQty = normalizationAliquotsData[ALIQUOTS_NUMBER]?.value;
    const aliquotsVolume = normalizationAliquotsData[ALIQUOTS_VOLUME]?.value;

    const includeNormalizationVolume = normalization === NORMALIZATION_OPTIONS.VOLUME ||
    normalization === NORMALIZATION_OPTIONS.CONCENTRATION_VOLUME;

    const includeNormalizationConcentration =
      normalization === NORMALIZATION_OPTIONS.CONCENTRATION ||
      normalization === NORMALIZATION_OPTIONS.CONCENTRATION_VOLUME;

    const processedData = getPooledPlatesListData(platePoolingData);

    const {
      hasRemainders,
      remainderTreatment,
      remainderPlateType,
      remainderVolume
    } = processRemainders(
      remaindersData,
      normalizationAliquotsData,
      platePoolingData
    );

    const aliquotsAllowed = hasAliquotsAllowed(normalization);

    const remaindersAllowed = hasRemaindersAllowed(
      normalization,
      platePoolingData[PLATE_POOLING]?.value
    );

    // set default null for nomral dna oligos.
    processedData.ampliseq = null;

    const dnaType = this.getDNAType();

    if (normalization === NORMALIZATION_OPTIONS.NONE) {
      processedData.normalizationType = normalization;
    }

    if (wellVolume) {
      processedData.wellVolume = wellVolume;
    }

    if (synthesisScale) {
      processedData.synthesisScale = synthesisScale;
    }

    if (purification) {
      processedData.purification = purification;
    }

    if (buffer) {
      processedData.buffer = buffer;
    }

    if (hasQualityTestingPercentage && qualityTestingPercentage) {
      processedData.qualityTestingPercentage = qualityTestingPercentage;
    }

    if (shipFormat) {
      processedData.shipFormat = shipFormat;
    }

    if (normalization &&
        volumeNormalization &&
        includeNormalizationVolume &&
        volumeNormalizationUom
    ) {
      processedData.normalizationType = normalization;
      processedData.volumeNormalization = volumeNormalization;
      processedData.volumeNormalizationUom = volumeNormalizationUom;
    }

    if (normalization &&
        concentrationNormalization &&
        includeNormalizationConcentration &&
        concentrationNormalizationUom
    ) {
      processedData.normalizationType = normalization;
      processedData.concentrationNormalization = concentrationNormalization;
      processedData.concentrationNormalizationUom = concentrationNormalizationUom;
    }

    if (hasAliquots && aliquotsAllowed) {
      processedData.hasAliquots = hasAliquots;
    }

    if (aliquotsQty && hasAliquots && aliquotsAllowed) {
      processedData.aliquotsQuantity = aliquotsQty;
    }

    if (aliquotsVolume && hasAliquots && aliquotsAllowed) {
      processedData.aliquotsVolume = aliquotsVolume;
    }

    if (hasRemainders && remaindersAllowed) {
      processedData.hasRemainders = hasRemainders;
    }

    if (remainderTreatment && hasRemainders && remaindersAllowed) {
      processedData.remainderTreatment = remainderTreatment;
    }

    if (remainderPlateType && hasRemainders && remaindersAllowed) {
      processedData.remainderPlateType = remainderPlateType;
    }

    if (remainderVolume && hasRemainders && remaindersAllowed) {
      processedData.remainderVolume = remainderVolume;
    }

    if ((plateSize === PLATE_SIZES.SIZE_96) && dnaType) {
      processedData.ampliseq = this.getAmpliseqType(dnaType);
    }

    return processedData;
  };

  static processSummaryApiData = (collectedData) => {
    const plateSizeData = collectedData[PLATE_SIZE_DETAILS_NAMESPACE] || {};
    const normalizationAliquotsData = collectedData[NORMALIZATION_ALIQUOTS_NAMESPACE] || {};
    const platePoolingData = collectedData[PLATE_POOLING_NAMESPACE] || {};
    const processedData = getPooledPlatesListData(platePoolingData);

    const purification = plateSizeData[PURIFICATION_FIELD]?.value;
    const hasAliquots = normalizationAliquotsData[ALIQUOTS_CHECKED]?.value;
    const normalization = normalizationAliquotsData[NORMALIZATION]?.value;

    if (purification) {
      processedData.purification = purification;
    }

    if (hasAliquots && hasAliquotsAllowed(normalization)) {
      processedData.hasAliquots = hasAliquots;
    }

    if (normalization && normalization !== NORMALIZATION_OPTIONS.NONE) {
      processedData.normalizationType = normalization;
    }

    return processedData;
  }

  static isDataValid(data, step) {
    const validatorByStep = {
      [STEPS.UPLOAD_FILE]: isUploadFileValid,
      [STEPS.PLATE_SIZE_DETAILS]: isPlateSizeDetailsValid,
      [STEPS.NORMALIZATION_ALIQUOTS]: isNormalizationAliquotsValid,
      [STEPS.PLATE_POOLING]: isPlatePoolingValid,
    };

    const validator = validatorByStep[step];

    if (!validator) {
      return true;
    }

    return !!validator(data);
  }
}
